import {
  DndContext,
  DragCancelEvent,
  DragEndEvent,
  DragStartEvent,
  MouseSensor,
  pointerWithin,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import { arrayMove } from '@dnd-kit/sortable';
import { useMutation } from '@tanstack/react-query';
import { equals, insertAll, remove } from 'ramda';
import React, { ReactNode, useCallback, useState } from 'react';
import toast from 'react-hot-toast';
import { useIntl } from 'react-intl';

import { updateItemsInPickingArea, updatePickingAreas } from 'apis/picker-api/api';
import { PickingArea } from 'apis/picker-api/types';

import { UNASSIGNED_AREA_ID } from '../constants';
import { ListElement, DnD, ItemData } from '../types';
import { filterOrphanHeaders, isItemType } from '../utils';

type Props = {
  venueId: string;
  pickingAreas: PickingArea[] | undefined;
  activeAreaId: string | undefined;
  activeAreaItems: ListElement[];
  onSetPickingAreas: (areas: PickingArea[]) => void;
  onSetActiveAreaItems: (items: ListElement[] | ((items: ListElement[]) => ListElement[])) => void;
  onAssignItemsToArea: ({ areaId, itemIds }: { areaId: string; itemIds: string[] }) => Promise<void>;
  onDropItemsInDropzone: (itemsData: ItemData[]) => void;
  children: ({
    draggedAreaId,
    draggedItemIds,
  }: {
    draggedAreaId: string | undefined;
    draggedItemIds: string[] | undefined;
  }) => ReactNode;
};

type DragEndSortableEvent = DragEndEvent & {
  active: { id: string; data: { current?: { type: string; itemIds?: string[] } } };
  over: { id: string; data: { current: { type: string } } } | null;
};

type DragStartSortableEvent = DragStartEvent & {
  active: { id: string; data: { current: { type: string; itemIds?: string[] } } };
};

const PickingAreasDnDContext = ({
  venueId,
  pickingAreas = [],
  activeAreaId,
  activeAreaItems,
  onSetPickingAreas,
  onSetActiveAreaItems,
  onAssignItemsToArea,
  onDropItemsInDropzone,
  children,
}: Props) => {
  const intl = useIntl();

  const [draggedItemIds, setDraggedItemIds] = useState<string[]>();
  const [draggedAreaId, setDraggedAreaId] = useState<string>();

  const [previousPickingAreas, setPreviousPickingAreas] = useState<PickingArea[]>(pickingAreas);
  const [previousItems, setPreviousItems] = useState<ListElement[]>(activeAreaItems);

  const sensors = useSensors(useSensor(MouseSensor, { activationConstraint: { distance: 5 } }));

  const reorderAreas = useMutation(
    async (orderedAreas: { id: string; order: number }[]) => {
      await updatePickingAreas(venueId, orderedAreas);
    },
    {
      onError: () => {
        onSetPickingAreas(previousPickingAreas);
        toast.error(intl.formatMessage({ id: 'picking-route.toast-generic-error' }));
      },
    },
  );

  const reorderItems = useMutation(
    async (orderedItems: { id: string; order: number }[]) => {
      if (!activeAreaId) {
        throw new Error('Active picking area not defined, this should not happen!');
      }
      await updateItemsInPickingArea(venueId, activeAreaId, orderedItems);
    },
    {
      onError: () => {
        onSetActiveAreaItems(previousItems);
        toast.error(intl.formatMessage({ id: 'picking-route.toast-generic-error' }));
      },
    },
  );

  const handleAssignItemsToArea = useCallback(
    async ({ areaId, itemIds }: { areaId: string; itemIds: string[] }) => {
      try {
        await onAssignItemsToArea({ areaId, itemIds });
      } catch (error) {
        onSetActiveAreaItems(previousItems);
      }
    },
    [onAssignItemsToArea, onSetActiveAreaItems, previousItems],
  );

  const handleDragStart = useCallback(
    (event: DragStartSortableEvent) => {
      const { type, itemIds } = event.active.data.current;
      if (type === DnD.Area) {
        setPreviousPickingAreas(pickingAreas);
        setDraggedAreaId(event.active.id);
      }
      if (type === DnD.SingleItem) {
        setPreviousItems(activeAreaItems);
        setDraggedItemIds([event.active.id]);
      }
      if (type === DnD.MultiItems) {
        setPreviousItems(activeAreaItems);
        setDraggedItemIds(itemIds);
        if (itemIds && activeAreaId !== UNASSIGNED_AREA_ID) {
          onSetActiveAreaItems(prev =>
            prev.filter(item => !itemIds.filter(itemId => itemId !== event.active.id).includes(item.data.id)),
          );
        }
      }
    },
    [activeAreaId, activeAreaItems, onSetActiveAreaItems, pickingAreas],
  );

  const handleDragCancel = useCallback(
    (event: DragCancelEvent) => {
      if (event.active.data.current?.type === DnD.Area) {
        setDraggedAreaId(undefined);
      }
      if (event.active.data.current?.type === DnD.SingleItem) {
        setDraggedItemIds(undefined);
      }
      if (event.active.data.current?.type === DnD.MultiItems) {
        setDraggedItemIds(undefined);
        onSetActiveAreaItems(previousItems);
      }
    },
    [onSetActiveAreaItems, previousItems],
  );

  const handleDragEnd = useCallback(
    async (event: DragEndSortableEvent) => {
      const { active, over } = event;

      // An issue with virtualized lists cause the active.data.current to get lost: https://github.com/clauderic/dnd-kit/issues/794
      // For that reason `dragItemTypeFromState` and `activeItemIds` are defined here from the local state
      const dragItemTypeFromState =
        (draggedItemIds?.length && (draggedItemIds.length === 1 ? DnD.SingleItem : DnD.MultiItems)) || undefined;
      const activeItemIds = active.data.current?.itemIds || draggedItemIds;

      const activeType = active.data.current?.type || dragItemTypeFromState;

      if (!over) {
        handleDragCancel(event);
        return;
      }

      const overType = over.data.current.type;

      if (activeType === DnD.Area && overType === DnD.Area) {
        setDraggedAreaId(undefined);
        if (active.id !== over.id) {
          const oldIndex = pickingAreas.findIndex(area => area.id === active.id);
          const newIndex = pickingAreas.findIndex(area => area.id === over.id);
          const nextAreas = arrayMove(pickingAreas, oldIndex, newIndex);
          onSetPickingAreas(nextAreas);
          await reorderAreas.mutateAsync([{ id: active.id, order: newIndex }]);
        }
      }

      if (activeType === DnD.SingleItem && (overType === DnD.SingleItem || overType === DnD.MultiItems)) {
        setDraggedItemIds(undefined);
        if (active.id !== over.id) {
          const oldIndex = activeAreaItems.findIndex(item => item.data.id === active.id);
          const newIndex = activeAreaItems.findIndex(item => item.data.id === over.id);
          const nextItems = arrayMove(activeAreaItems, oldIndex, newIndex);
          onSetActiveAreaItems(nextItems);
          await reorderItems.mutateAsync([{ id: active.id, order: newIndex + 1 }]);
        }
      }

      if (activeType === DnD.MultiItems && (overType === DnD.SingleItem || overType === DnD.MultiItems)) {
        setDraggedItemIds(undefined);
        const itemIds = activeItemIds || [active.id];
        const oldIndex = activeAreaItems.findIndex(item => item.data.id === active.id);
        const newIndex = activeAreaItems.findIndex(item => item.data.id === over.id);
        const insertedItems = previousItems.filter(item => itemIds.includes(item.data.id)) || [];
        const nextItems = insertAll(newIndex, insertedItems, remove(oldIndex, 1, activeAreaItems));
        if (!equals(nextItems, previousItems)) {
          onSetActiveAreaItems(nextItems);
          await reorderItems.mutateAsync(
            nextItems.map((item, index) => ({ id: item.data.id, order: newIndex + index + 1 })),
          );
        } else {
          onSetActiveAreaItems(previousItems);
        }
      }

      if (activeType === DnD.SingleItem && overType === DnD.Area) {
        setDraggedItemIds(undefined);
        if (activeAreaId === over.id) {
          return;
        }
        const nextItems = activeAreaItems.filter(item => item.data.id !== active.id).filter(filterOrphanHeaders);
        onSetActiveAreaItems(nextItems);
        await handleAssignItemsToArea({ areaId: over.id, itemIds: [active.id] });
      }

      if (activeType === DnD.MultiItems && overType === DnD.Area) {
        setDraggedItemIds(undefined);
        if (activeAreaId === over.id) {
          onSetActiveAreaItems(previousItems);
          return;
        }
        const itemIds = activeItemIds || [active.id];
        const nextItems = activeAreaItems.filter(item => !itemIds.includes(item.data.id)).filter(filterOrphanHeaders);
        onSetActiveAreaItems(nextItems);
        await handleAssignItemsToArea({ areaId: over.id, itemIds });
      }

      if ((activeType === DnD.SingleItem || activeType === DnD.MultiItems) && overType === DnD.Dropzone) {
        setDraggedItemIds(undefined);
        const { active } = event;
        const itemIds = activeItemIds || [active.id];
        onDropItemsInDropzone(activeAreaItems.filter(isItemType).filter(item => itemIds.includes(item.data.id)));
      }
    },
    [
      activeAreaId,
      activeAreaItems,
      draggedItemIds,
      handleAssignItemsToArea,
      handleDragCancel,
      onDropItemsInDropzone,
      onSetActiveAreaItems,
      onSetPickingAreas,
      pickingAreas,
      previousItems,
      reorderAreas,
      reorderItems,
    ],
  );

  return (
    <DndContext
      onDragStart={handleDragStart}
      onDragEnd={handleDragEnd}
      onDragCancel={handleDragCancel}
      collisionDetection={pointerWithin}
      sensors={sensors}
    >
      {children({ draggedAreaId, draggedItemIds })}
    </DndContext>
  );
};

export default PickingAreasDnDContext;
